import { Middleware } from 'redux';

import { websocketActions } from 'state/websockets';
import { AuthActions } from 'state/auth';
import { antennasActions } from 'state/antennas';
import { WEBSOCKET_URL } from 'config';
import { WebSocketManager } from 'models';
import { groundstationsActions } from 'state/groundstations';
import { switchesActions } from 'state/switches';
import { sensorsActions } from 'state/sensors';

const RECONNECTION_ATTEMPTS = 5;

// eslint-disable-next-line @typescript-eslint/ban-types
const websocketMiddleware: Middleware = ({ dispatch }) => {
    let socket: WebSocket;
    let accessToken: string | null = null;
    let tokenType: string | null = null;

    let reconnectionAttempt = 0;

    return (next) => (action) => {
        if (AuthActions.setTokens.match(action)) {
            accessToken = action.payload.accessToken;
            tokenType = action.payload.tokenType;
        }

        if (websocketActions.sendData.match(action)) {
            if (!socket) return next(action);

            socket.send(JSON.stringify(action.payload));
        }

        if (websocketActions.disconnect.match(action)) {
            if (!socket) return next(action);

            socket.close(1000, 'Closed by User');
        }

        if (websocketActions.connect.match(action)) {
            // We are not authenticated
            if (!accessToken || !tokenType) return next(action);

            if (reconnectionAttempt > RECONNECTION_ATTEMPTS) {
                dispatch(
                    websocketActions.error({
                        message:
                            'Unable to reconnect to Server - Reconnection Attempts Exceeded',
                    }),
                );
                return next(action);
            }

            try {
                socket = new WebSocket(
                    `${WEBSOCKET_URL}?token=${tokenType} ${accessToken}`,
                );
                reconnectionAttempt++;
            } catch (err) {
                reconnectionAttempt++;
            }

            socket.addEventListener('open', () => {
                reconnectionAttempt = 0;
                dispatch(websocketActions.connectionEstablished());
            });

            socket.addEventListener('message', (event) => {
                const data = JSON.parse(
                    event.data,
                ) as WebSocketManager.IncomingCommandType;

                console.log(data);

                // Now depending on the 'command', we do something
                if (!data.command) {
                    // If no command, assume general message
                    dispatch(websocketActions.receiveMessage(data));
                }

                if (
                    data.command === 'message' ||
                    data.command === 'send_message'
                ) {
                    dispatch(websocketActions.receiveMessage(data));
                }

                if (data.command === 'update_antenna_position') {
                    dispatch(antennasActions.updateAntennaPosition(data));
                }

                if (data.command === 'update_antenna_status') {
                    dispatch(antennasActions.updateAntennaStatus(data));
                }

                if (data.command === 'update_ground_station_status') {
                    dispatch(antennasActions.updateAntennaConnected(data));
                }

                if (data.command === 'command_updated') {
                    dispatch(
                        groundstationsActions.setGroundStationCommandLastUpdated(
                            data,
                        ),
                    );
                }

                if (data.command === 'update_switch') {
                    dispatch(switchesActions.onSwitchValueUpdated(data));
                }

                if (data.command === 'add_sensor_data') {
                    dispatch(sensorsActions.onAddSensorData(data));
                }
            });

            socket.addEventListener('close', () => {
                dispatch(websocketActions.disconnected());

                if (reconnectionAttempt < RECONNECTION_ATTEMPTS) {
                    dispatch(websocketActions.connect());
                } else {
                    dispatch(
                        websocketActions.error({
                            message: 'Unable to reconnect to Server',
                        }),
                    );
                }
            });

            socket.addEventListener('error', () => {
                dispatch(websocketActions.disconnected());

                if (reconnectionAttempt < RECONNECTION_ATTEMPTS) {
                    dispatch(websocketActions.connect());
                } else {
                    dispatch(
                        websocketActions.error({
                            message: 'Unable to reconnect to Server',
                        }),
                    );
                }
            });
        }

        next(action);
    };
};

export default websocketMiddleware;
